/**
 * ALDER TALENT — VP & Specialist Search Page
 * URL: /senior-leadership/vp-specialist-search
 */

import { useEffect } from "react";
import { Link } from "wouter";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useFaqSchema } from "@/hooks/useFaqSchema";

const vpSpecialistFaqs = [
  {
    question: "What VP and senior IC roles do you place in VC- and PE-backed businesses?",
    answer: "We place VP Product, Staff and Principal Product Managers, and Lead Product Managers in VC- and PE-backed businesses. The common thread is deep product expertise and the ability to deliver in a high-accountability, investor-backed environment.",
  },
  {
    question: "How does a VP or senior IC search differ from a C-suite search?",
    answer: "VP and senior IC searches focus on product leaders and practitioners who own a specific function, discipline, or product area. The assessment criteria emphasise functional depth, product judgement, and the ability to execute within a defined strategic framework.",
  },
  {
    question: "What makes a strong product candidate for a VC- and PE-backed business?",
    answer: "A strong product candidate combines deep functional expertise with the ability to operate in a high-accountability, fast-paced environment. They must be able to make decisions under resource constraints, connect customer outcomes to commercial priorities, and communicate performance clearly to leadership and investors. Previous experience in a VC- and PE-backed or venture-backed business is a significant advantage.",
  },
  {
    question: "How long does a VP or senior IC search take?",
    answer: "A retained VP or senior IC search typically delivers a shortlist in four to six weeks and completes placement in seven to ten weeks. The timeline depends on the seniority and specialisation of the role, the sector, and the depth of the candidate market.",
  },
  {
    question: "Do you run product searches across all sectors?",
    answer: "We run product searches across all sectors where VC and PE investors hold portfolio companies. Our focus is always VC- and PE-backed businesses, and we assess candidates against the specific product and customer challenges of the portfolio company's stage and sector.",
  }
];


const steps = [
  {
    num: "01",
    title: "Role Definition",
    body: "We begin by defining what this role needs to achieve — not what the job description says. What does a VP Product, Staff Product Manager, Principal Product Manager, or Lead Product Manager need to deliver in year one at this specific portfolio company? That shapes who we look for.",
  },
  {
    num: "02",
    title: "Candidate Universe Mapping",
    body: "We map the specific talent pool for this role. Specialist roles have narrow candidate universes. We know them. This step takes longer than a standard brief — because precision here saves weeks downstream.",
  },
  {
    num: "03",
    title: "Targeted Outreach",
    body: "We approach candidates personally. Specialist searches don't benefit from volume. Every candidate we contact has been assessed as a potential fit before outreach begins.",
  },
  {
    num: "04",
    title: "Deep Assessment",
    body: "We go deeper on assessment for product roles than generalist ones. The functional knowledge needs to be genuine. We validate product judgement through structured conversation and peer-level reference — not just competency interviews.",
  },
  {
    num: "05",
    title: "Shortlist",
    body: "Two to three candidates. Quality over quantity. Each comes with a full written assessment of product fit and VC and PE operating suitability.",
  },
  {
    num: "06",
    title: "Placement",
    body: "We manage offer and onboarding. Product roles are often pivotal hires in portfolio companies. We set the right expectations on both sides for the first 90 days.",
  },
];

const outcomes = [
  "A retained, bespoke search for a product role most firms try — and fail — to fill through contingency or advertising.",
  "Genuine market coverage including passive candidates who'll never appear on a job board.",
  "A product leader or practitioner placed against your specific business context — not a generic job description.",
];

export default function VpSpecialistPage() {
  useFaqSchema(vpSpecialistFaqs, "vp-faq-jsonld");
  useEffect(() => {
    const prev = document.title;
    document.title = "VP Product & Senior IC Search for VC- and PE-Backed Companies | Alder Talent";
    const meta = document.querySelector('meta[name="description"]');
    const prevContent = meta?.getAttribute("content") ?? "";
    meta?.setAttribute(
      "content",
      "Retained search for VP Product and senior product IC roles in VC- and PE-backed businesses. Hard to find. We find them."
    );
    return () => {
      document.title = prev;
      meta?.setAttribute("content", prevContent);
    };
  }, []);

  const handleEngageClick = () => {
    const el = document.querySelector("#contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else window.location.href = "/#contact";
  };

  return (
    <div className="min-h-screen" style={{ background: "oklch(0.97 0.006 80)" }}>
      <Navigation />

      {/* HERO */}
      <section
        className="relative pt-40 pb-24 overflow-hidden"
        style={{ background: "linear-gradient(160deg, oklch(0.18 0.055 155) 0%, oklch(0.245 0.065 155) 100%)" }}
      >
        <div className="container relative z-10">
          <div className="mb-4 flex items-center gap-2" style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.14em", textTransform: "uppercase" }}>
            <Link href="/" style={{ color: "oklch(0.55 0.02 155)", textDecoration: "none" }} onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.72 0.09 75)"; }} onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.55 0.02 155)"; }}>Home</Link>
            <span style={{ color: "oklch(0.38 0.07 155)" }}>›</span>
            <Link href="/senior-leadership" style={{ color: "oklch(0.55 0.02 155)", textDecoration: "none" }} onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.72 0.09 75)"; }} onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.55 0.02 155)"; }}>Senior Leadership</Link>
            <span style={{ color: "oklch(0.38 0.07 155)" }}>›</span>
            <span style={{ color: "oklch(0.62 0.085 75)" }}>VP Product &amp; Senior IC Search</span>
          </div>
          <h1 className="mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(2.2rem, 5vw, 3.8rem)", lineHeight: 1.1, color: "oklch(0.97 0.006 80)", maxWidth: "760px" }}>
            The Product Roles That Don't Fill Themselves.
          </h1>
          <p className="mb-10" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "clamp(1rem, 2vw, 1.15rem)", lineHeight: 1.75, color: "oklch(0.78 0.018 80)", maxWidth: "580px" }}>
            VP Product. Staff Product Manager. Principal Product Manager. Lead Product Manager. These aren't roles you fill from a job board. The candidates are specific, the market is shallow, and the wrong hire is expensive. We place niche, high-calibre product leaders and practitioners in the roles that VC- and PE-backed businesses rely on.
          </p>
          <div className="flex flex-wrap gap-4">
            <button onClick={handleEngageClick} className="px-8 py-3.5 transition-all duration-300"
              style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.75rem", background: "oklch(0.62 0.085 75)", color: "oklch(0.18 0.055 155)", border: "none", borderRadius: "2px" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.72 0.09 75)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.62 0.085 75)"; }}>
              Engage Alder Talent
            </button>
            <Link href="/senior-leadership" className="px-8 py-3.5 transition-all duration-300"
              style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: "0.75rem", background: "transparent", color: "oklch(0.88 0.012 80)", border: "1px solid oklch(0.45 0.07 155)", borderRadius: "2px", textDecoration: "none" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "oklch(0.62 0.085 75)"; (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.62 0.085 75)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "oklch(0.45 0.07 155)"; (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.88 0.012 80)"; }}>
              ← Back to Senior Leadership
            </Link>
          </div>
        </div>
      </section>

      {/* OUTCOMES STRIP */}
      <section className="py-14" style={{ background: "oklch(0.245 0.065 155)", borderBottom: "1px solid oklch(0.32 0.07 155)" }}>
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {outcomes.map((text, i) => (
              <div key={i} className="flex gap-4 items-start">
                <div className="mt-1 shrink-0" style={{ width: "20px", height: "20px", borderRadius: "50%", background: "oklch(0.62 0.085 75)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="oklch(0.18 0.055 155)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
                <p style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "0.92rem", lineHeight: 1.65, color: "oklch(0.82 0.015 80)" }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS SECTION */}
      <section className="py-24" style={{ background: "oklch(0.97 0.006 80)" }}>
        <div className="container">
          <div className="mb-3" style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "oklch(0.62 0.085 75)" }}>How This Search Works</div>
          <h2 className="mb-5" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", lineHeight: 1.15, color: "oklch(0.245 0.065 155)", maxWidth: "640px" }}>
            Product Search Built Around the Role — Not the Job Description.
          </h2>
          <p className="mb-16" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "1rem", lineHeight: 1.75, color: "oklch(0.42 0.03 155)", maxWidth: "560px" }}>
            Product roles fail when they're filled from a generic brief. We start by understanding what the role actually needs to deliver.
          </p>
          <div className="flex flex-col gap-0">
            {steps.map((step) => (
              <div key={step.num} className="grid grid-cols-1 md:grid-cols-[80px_1fr] gap-4 py-8" style={{ borderTop: "1px solid oklch(0.88 0.01 80)" }}>
                <div style={{ fontFamily: "'DM Mono', monospace", fontWeight: 400, fontSize: "0.72rem", letterSpacing: "0.1em", color: "oklch(0.62 0.085 75)", paddingTop: "3px" }}>{step.num}</div>
                <div>
                  <h3 className="mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 600, fontSize: "1.05rem", color: "oklch(0.245 0.065 155)" }}>{step.title}</h3>
                  <p style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "0.92rem", lineHeight: 1.75, color: "oklch(0.42 0.03 155)" }}>{step.body}</p>
                </div>
              </div>
            ))}
            <div style={{ borderTop: "1px solid oklch(0.88 0.01 80)" }} />
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="py-20" style={{ background: "oklch(0.18 0.055 155)" }}>
        <div className="container">
          <div className="mx-auto" style={{ maxWidth: "680px", textAlign: "center" }}>
            <div className="mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic", fontSize: "clamp(1.15rem, 2.5vw, 1.55rem)", lineHeight: 1.6, color: "oklch(0.88 0.012 80)" }}>
              "Specialist roles sit empty for months when they're run as contingency searches. We run them as retained. That's why they fill."
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "oklch(0.62 0.085 75)" }}>
              Alder Talent, Senior Leadership Practice
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section id="contact" className="py-24" style={{ background: "oklch(0.245 0.065 155)" }}>
        <div className="container text-center">
          <h2 className="mb-5" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", lineHeight: 1.15, color: "oklch(0.97 0.006 80)" }}>
            Got a Product Role That's Resisted the Standard Approaches?
          </h2>
          <p className="mb-10 mx-auto" style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 300, fontSize: "1rem", lineHeight: 1.75, color: "oklch(0.72 0.018 80)", maxWidth: "520px" }}>
            If a contingency search or advertising campaign hasn't delivered — let's talk about running it properly.
          </p>
          <button onClick={handleEngageClick} className="px-10 py-4 transition-all duration-300"
            style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.75rem", background: "oklch(0.62 0.085 75)", color: "oklch(0.18 0.055 155)", border: "none", borderRadius: "2px" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.72 0.09 75)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.62 0.085 75)"; }}>
            Engage Alder Talent
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
